import React, { useState, useEffect } from 'react';
import { CreditCard, Settings, FileText, ExternalLink, ChevronRight, Clock } from 'lucide-react';
import { Button } from '../components/common/Button';
import { GeneralSettings } from '../components/settings/GeneralSettings';
import { PreferenceSettings } from '../components/settings/PreferenceSettings';
import { GoogleCalendarSettings } from '../components/settings/GoogleCalendarSettings';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ParticleBackground } from '../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { useAuthStore } from '../store';


// Define props for the SettingsPage component
interface SettingsPageProps {
  subscriptionInfo: {
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
    planName: string;
    isActive: boolean;
    expiryDate: string;
    minutesLeft: number;
  };
  isLoadingSubscription?: boolean;
  onSubscriptionUpdate?: () => void;
}

// SettingsPage component
const SettingsPage: React.FC<SettingsPageProps> = ({ 
  subscriptionInfo,
  isLoadingSubscription = false,
  onSubscriptionUpdate
}) => {
  // State to track active tab
  const [activeTab, setActiveTab] = useState<'settings' | 'preferences'>('settings');
  
  // Get user data from auth store 
  const { user: storeUser } = useAuthStore();
  // Also check the auth context for backward compatibility
  const { user: contextUser } = useAuth();
  
  // Use location to access URL query parameters
  const location = useLocation();
  
  // Use store user if available, otherwise fallback to context user
  const user = storeUser || contextUser;
  
  // Create a safe user object with fallbacks
  const currentUser = user ? {
    name: user.name || user.email?.split('@')[0] || 'User',
    email: user.email || 'guest@example.com',
    profileImage: user.profilePicture || '',
    subscriptionPlan: subscriptionInfo.planName || 'Free Plan',
    remainingMinutes: subscriptionInfo.minutesLeft || 0
  } : {
    name: 'User',
    email: 'guest@example.com',
    profileImage: '',
    subscriptionPlan: 'Free Plan',
    remainingMinutes: 0
  };
  
  const navigate = useNavigate();

  // Check for tab query parameter when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    
    if (tabParam === 'preferences') {
      setActiveTab('preferences');
    }
  }, [location.search]);

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  const [imgError, setImgError] = useState(false);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <div className="container mx-auto px-4 py-12 max-w-7xl mt-20">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <PageTransitionItem index={1}>
            <div className="w-full lg:w-60 shrink-0">
              <div className="sticky top-24 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden">
                {/* Navigation Tabs */}
                <nav className="flex flex-col">
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      activeTab === 'settings'
                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark border-l-3 border-primary-light dark:border-primary-dark'
                        : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light dark:hover:bg-background-dark border-l-3 border-transparent'
                    }`}
                  >
                    <Settings size={16} />
                    <span className="font-medium text-base">Settings</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('preferences')}
                    className={`flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      activeTab === 'preferences'
                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark border-l-3 border-primary-light dark:border-primary-dark'
                        : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light dark:hover:bg-background-dark border-l-3 border-transparent'
                    }`}
                  >
                    <FileText size={16} />
                    <span className="font-medium text-base">Profile Preferences</span>
                  </button>
                </nav>
              </div>
            </div>
          </PageTransitionItem>

          {/* Main Content */}
          <div className="flex-1">
            <PageTransitionItem index={2}>
              <div className="mb-6">
                <h1 className="text-2xl font-bold mb-1">{activeTab === 'settings' ? 'Settings' : activeTab === 'preferences' ? 'Profile Preferences' : 'Subscription'}</h1>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  {activeTab === 'settings' 
                    ? 'Manage your account settings and preferences' 
                    : activeTab === 'preferences'
                      ? 'Configure your resume and career preferences'
                      : 'Manage your subscription'}
                </p>
              </div>
            </PageTransitionItem>

            {/* User Profile */}
            {activeTab === 'settings' && (
              <PageTransitionItem index={3}>
                <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-4 md:p-5 mb-6">
                  <div className="flex flex-col md:flex-row gap-5 items-start">
                    {/* Profile Image */}
                    <div className={`w-16 h-16 rounded-full overflow-hidden flex-shrink-0 ${imgError ? 'bg-secondary-light dark:bg-secondary-dark/70' : 'bg-background-light dark:bg-background-dark'}`}>
                      {currentUser.profileImage && !imgError ? (
                        <img 
                          src={currentUser.profileImage} 
                          alt={currentUser.name} 
                          className="w-full h-full object-cover"
                          onError={() => setImgError(true)}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70">
                          <span className="text-white text-3xl font-bold font-heading">{currentUser.name?.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1">
                      <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-1">{currentUser.name.charAt(0).toUpperCase() + currentUser.name.slice(1)}</h2>
                      <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-2">{currentUser.email}</p>
                      
                      {/* Subscription Info */}
                      <div className="flex items-center gap-2">
                        <CreditCard size={16} className={`${currentUser.subscriptionPlan === 'Free Plan' ? 'text-primary-light dark:text-primary-dark' : 'text-green-500 dark:text-green-400'}`} />
                        <span className="text-sm font-medium">{isLoadingSubscription ? 'Loading...' : currentUser.subscriptionPlan}</span>
                        {!isLoadingSubscription && (
                          <span className="w-auto flex items-center justify-center text-sm bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark py-1 px-3 rounded-full">
                            <Clock size={16} className="mr-1"/>
                            {currentUser.remainingMinutes} Mins remaining
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 w-full md:w-auto">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="py-3"
                        rightIcon={<ChevronRight size={16} />}
                        onClick={() => navigate('/subscription')}
                      >
                        <span className="text-sm">Manage Subscription</span>
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="py-3"
                        rightIcon={<ExternalLink size={16} />}
                        onClick={() => window.open('https://dashboard.jobprot.com', '_blank')}
                      >
                        <span className="text-sm">Manage Account</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </PageTransitionItem>
            )}

            {/* Settings Content */}
            {activeTab === 'settings' && (
              <PageTransitionItem index={4}>
                <GoogleCalendarSettings />
                <GeneralSettings />
              </PageTransitionItem>
            )}
            
            {/* Preferences Content */}
            {activeTab === 'preferences' && (
              <PageTransitionItem index={4}>
                <PreferenceSettings />
              </PageTransitionItem>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

SettingsPage.displayName = 'SettingsPage';

// Export with page transition HOC
export default withPageTransition(SettingsPage);